import "../Styles/Admin/EmailSettings.css";
import { API_BASE_URL } from "../apiConfig";
import { useNavigate } from "react-router-dom";

import React, { useState, useEffect, useCallback } from "react";
import Header from "../Components/Common/Header";
import { Mail, Save, RefreshCw, CheckCircle, AlertCircle, HelpCircle } from "lucide-react";

const EmailSettings = () => {
  const [settings, setSettings] = useState({
    email: "",
    password: ""
  });
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ type: '', message: '' });
  
  const companyId = localStorage.getItem('companyId');
  const navigate = useNavigate();

  const fetchEmailSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/email-settings/${companyId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('idToken')}`
        }
      });
      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('idToken');
        navigate('/');
        return;
      }
      const data = await response.json();
      
      if (data.success) {
        setSettings(data.data);
      }
    } catch (error) {
      setNotification({ 
        type: 'error', 
        message: 'Failed to load email settings' 
      });
    } finally {
      setLoading(false);
    }
  }, [companyId, navigate]);

  useEffect(() => {
    fetchEmailSettings();
  }, [fetchEmailSettings]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/email-settings/${companyId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('idToken')}`
        },
        body: JSON.stringify(settings)
      });
      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('idToken');
        navigate('/');
        return;
      }
      const data = await response.json();
      
      if (data.success) {
        setNotification({ 
          type: data.warning ? 'warning' : 'success', 
          message: data.message 
        });
      } else {
        setNotification({ 
          type: 'error', 
          message: data.message || 'Failed to save settings' 
        });
      }
    } catch (error) {
      setNotification({ 
        type: 'error', 
        message: 'An error occurred while saving settings' 
      });
    } finally {
      setIsSaving(false);
      setTimeout(() => setNotification({ type: '', message: '' }), 3000);
    }
  };

  if (loading) {
    return <div className="email-settings-container">Loading...</div>;
  }

  return (
    <div className="email-settings-container">
      <div className="ap-wrap-content">
        <Header 
          title="Email Settings" 
          description="Configure email for monthly attendance reports" 
          icon={<Mail size={28} color="#f3f4f6" />}
        />
        
        {notification.message && (
          <div className={`email-notification ${notification.type}`}>
            {notification.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
            <span>{notification.message}</span>
          </div>
        )}
        
        <div className="email-settings-card">
          <div className="email-info-box">
            <HelpCircle size={20} />
            <p>
              For Gmail accounts, you'll need to use an App Password instead of your regular password.
              <br />
              1. Enable 2-Step Verification in your Google Account
              <br />
              2. Create an App Password in Google Account settings
              <br />
              3. Use that App Password here
            </p>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">Gmail Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={settings.email}
                onChange={handleChange}
                placeholder="Enter Gmail address"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password">App Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={settings.password}
                onChange={handleChange}
                placeholder="Enter App Password"
                required
              />
              <small className="password-hint">Use App Password, not your regular Gmail password</small>
            </div>
            
            <button type="submit" className="save-button" disabled={isSaving}>
              {isSaving ? (
                <>
                  <RefreshCw size={16} className="spinner" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save size={16} />
                  <span>Save Settings</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmailSettings;
